/* Website enquiry forms (Get a Price, Free Kitchen Plan).
 *
 * Any <form data-enquiry-form="<key>"> on the page. The details go as JSON
 * through the App Proxy to btk-planner-api (/apps/planner/enquiry/<key>),
 * which files them in Odoo as a lead. A form with file pickers gets an upload
 * token back and then sends each file on its own (/apps/planner/enquiry/file),
 * so a file that fails never loses the enquiry. Photos are shrunk here first.
 *
 * Markup contract:
 *   form[data-enquiry-form]  data-signed-in="true|false", data-phone (shown in errors)
 *   [data-enq-msg="<name>"]  where a field's error message goes
 *   input[type=file][data-kind][data-max]  file pickers (required = at least one)
 *   [data-enq-files="<kind>"] the list of chosen files
 *   [data-enq-style] / [data-enq-style-field]  ?style=&style_name= handling
 *   [data-enq-done] the thank-you panel ([data-enq-done-name], [data-enq-done-warn])
 */
(function () {
  'use strict';

  var MAX_FILE_BYTES = 3.5 * 1024 * 1024;   // the server's limit, per request
  var MAX_UPLOAD_BYTES = 35 * 1024 * 1024;  // the server's limit, per file sent in parts
  var PART_BYTES = 3 * 1024 * 1024;         // a part, comfortably under one request
  var MAX_PHOTO_EDGE = 2400;                // longest side after shrinking
  var SHRINK_OVER = 900 * 1024;             // photos bigger than this are shrunk
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var API = '/apps/planner/enquiry/';

  function init(form) {
    var key = form.getAttribute('data-enquiry-form');
    var signedIn = form.getAttribute('data-signed-in') === 'true';
    var phoneNo = form.getAttribute('data-phone') || '';
    var shownAt = Date.now();
    var root = form.closest('[data-enq-root]') || document;
    var errBox = form.querySelector('[data-enq-error]');
    var submit = form.querySelector('[type=submit]');
    var submitLabel = submit.textContent;
    var styleBox = root.querySelector('[data-enq-style]');
    var styleField = form.querySelector('[data-enq-style-field]');
    var done = root.querySelector('[data-enq-done]');
    var style = '';
    var picked = {};   // kind -> [File]

    // ── Kitchen style from the link: ?style=<handle>&style_name=<name> ──────
    if (styleBox && form.elements.style_name) {
      try {
        var q = new URLSearchParams(window.location.search);
        var handle = (q.get('style') || '').toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 80);
        var styleName = (q.get('style_name') || '').slice(0, 80);
        if (handle && styleName) {
          style = handle;
          styleBox.querySelector('[data-enq-style-name]').textContent = styleName;
          form.elements.style_name.value = styleName;
          styleBox.hidden = false;
          if (styleField) styleField.hidden = true;
        }
      } catch (e) { style = ''; }
      var clear = styleBox.querySelector('[data-enq-style-clear]');
      if (clear) clear.addEventListener('click', function () {
        style = '';
        form.elements.style_name.value = '';
        styleBox.hidden = true;
        if (styleField) styleField.hidden = false;
        form.elements.style_name.focus();
      });
    }

    // ── Messages ────────────────────────────────────────────────────────────
    function fieldMsg(name, text) {
      var el = form.querySelector('[data-enq-msg="' + name + '"]');
      if (!el) return false;
      el.textContent = text || '';
      var f = el.closest('.btk-enq__field');
      if (f) f.classList.toggle('is-invalid', !!text);
      return true;
    }
    function clearMsgs() {
      form.querySelectorAll('[data-enq-msg]').forEach(function (el) { fieldMsg(el.getAttribute('data-enq-msg'), ''); });
      errBox.hidden = true;
      errBox.textContent = '';
    }
    function showError(text) { errBox.textContent = text; errBox.hidden = false; }
    function trouble() {
      return 'Sorry, we could not send your enquiry just now. Please try again'
        + (phoneNo ? ' or call us on ' + phoneNo : '') + '.';
    }
    function busy(label) {
      submit.disabled = !!label;
      submit.textContent = label || submitLabel;
    }

    // ── File pickers ────────────────────────────────────────────────────────
    function sizeLabel(n) { return n >= 1048576 ? (n / 1048576).toFixed(1) + 'MB' : Math.max(1, Math.round(n / 1024)) + 'KB'; }
    function isPhoto(f) { return /^image\//.test(f.type) || /\.(jpe?g|png|webp|heic|heif)$/i.test(f.name); }
    function isPdf(f) { return f.type === 'application/pdf' || /\.pdf$/i.test(f.name); }
    function isDoc(f) { return /\.docx$/i.test(f.name); }
    function renderList(kind) {
      var list = form.querySelector('[data-enq-files="' + kind + '"]');
      if (!list) return;
      list.innerHTML = '';
      (picked[kind] || []).forEach(function (f, i) {
        var li = document.createElement('li');
        var name = document.createElement('span'); name.textContent = f.name;
        var size = document.createElement('small'); size.textContent = sizeLabel(f.size);
        var rm = document.createElement('button'); rm.type = 'button'; rm.textContent = 'Remove';
        rm.setAttribute('aria-label', 'Remove ' + f.name);
        rm.addEventListener('click', function () { picked[kind].splice(i, 1); renderList(kind); });
        li.appendChild(name); li.appendChild(size); li.appendChild(rm);
        list.appendChild(li);
      });
    }
    form.querySelectorAll('input[type=file][data-kind]').forEach(function (input) {
      var kind = input.getAttribute('data-kind');
      var max = Number(input.getAttribute('data-max') || 1);
      picked[kind] = [];
      var drop = input.closest('.btk-enq__drop');
      input.addEventListener('change', function () {
        fieldMsg(kind, '');
        var add = Array.prototype.slice.call(input.files || []);
        input.value = '';
        var refused = [];
        add.forEach(function (f) {
          // Word documents only where the picker offers them (quote, measurements).
          var takesDocs = /\.docx/.test(input.getAttribute('accept') || '');
          if (!isPhoto(f) && !isPdf(f) && !(takesDocs && isDoc(f))) {
            refused.push(f.name + ' is not a photo' + (takesDocs ? ', PDF or Word document' : ' or PDF')); return;
          }
          if (f.size > MAX_UPLOAD_BYTES) { refused.push(f.name + ' is over 35MB'); return; }
          if (picked[kind].length >= max) { refused.push('You can add up to ' + max); return; }
          picked[kind].push(f);
        });
        if (refused.length) fieldMsg(kind, refused[0] + '.');
        renderList(kind);
      });
      if (drop) {
        ['dragenter', 'dragover'].forEach(function (t) { drop.addEventListener(t, function () { drop.classList.add('is-over'); }); });
        ['dragleave', 'drop'].forEach(function (t) { drop.addEventListener(t, function () { drop.classList.remove('is-over'); }); });
      }
    });

    // A photo as a JPEG no bigger than it needs to be. Anything the browser
    // cannot draw (HEIC outside Safari, say) goes as it is, if it fits.
    function shrink(file) {
      if (!isPhoto(file) || file.size <= SHRINK_OVER || !window.createImageBitmap) return Promise.resolve(file);
      return createImageBitmap(file).then(function (bmp) {
        var scale = Math.min(1, MAX_PHOTO_EDGE / Math.max(bmp.width, bmp.height));
        var c = document.createElement('canvas');
        c.width = Math.round(bmp.width * scale);
        c.height = Math.round(bmp.height * scale);
        c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
        if (bmp.close) bmp.close();
        return new Promise(function (resolve) { c.toBlob(resolve, 'image/jpeg', 0.85); });
      }).then(function (blob) {
        return blob ? new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }) : file;
      }).catch(function () { return file; });
    }
    function toBase64(blob) {
      return new Promise(function (resolve, reject) {
        var r = new FileReader();
        r.onload = function () { resolve(String(r.result).split(',')[1] || ''); };
        r.onerror = function () { reject(r.error); };
        r.readAsDataURL(blob);
      });
    }
    function postJSON(path, body) {
      return fetch(API + path, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (data) { return { res: res, data: data }; });
      });
    }
    // One file: whole if it fits in a request, otherwise in parts that the
    // server puts back together (a long quote PDF). Resolves with the last
    // answer; stops at the first part that is refused.
    function sendFile(token, kind, f, label) {
      if (f.size <= MAX_FILE_BYTES) {
        return toBase64(f).then(function (data) {
          return postJSON('file', { token: token, kind: kind, filename: f.name, data: data });
        });
      }
      if (f.size > MAX_UPLOAD_BYTES) return Promise.reject(new Error('too big'));
      var parts = Math.ceil(f.size / PART_BYTES);
      var upload = 'u' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      function part(n) {
        busy(label + ', ' + Math.round((n / parts) * 100) + '%)…');
        return toBase64(f.slice(n * PART_BYTES, (n + 1) * PART_BYTES)).then(function (data) {
          return postJSON('file', { token: token, kind: kind, filename: f.name, data: data,
            upload: upload, part: n, parts: parts });
        }).then(function (r) {
          return (!r.res.ok || n + 1 >= parts) ? r : part(n + 1);
        });
      }
      return part(0);
    }

    // Every chosen file, one at a time. Resolves with the names that failed.
    function uploadAll(token) {
      var queue = [];
      Object.keys(picked).forEach(function (kind) { picked[kind].forEach(function (f) { queue.push([kind, f]); }); });
      var failed = [];
      var i = 0;
      function next() {
        if (i >= queue.length) return Promise.resolve(failed);
        var kind = queue[i][0], file = queue[i][1];
        i += 1;
        var label = 'Uploading Files (' + i + ' of ' + queue.length;
        busy(label + ')…');
        return shrink(file).then(function (f) {
          return sendFile(token, kind, f, label);
        }).then(function (r) {
          if (!r.res.ok) failed.push(file.name);
        }).catch(function () { failed.push(file.name); }).then(next);
      }
      return next();
    }

    // ── Checks the server also makes, so people see them before sending ────
    function check(v) {
      var bad = [];
      form.querySelectorAll('[required]').forEach(function (el) {
        if (el.type === 'file') {
          var kind = el.getAttribute('data-kind');
          if (!(picked[kind] || []).length) bad.push([kind, el.getAttribute('data-required-msg') || 'Please add a file']);
          return;
        }
        if (el.type === 'email' || el.name === 'phone' || el.type === 'radio') return;
        if (!String(v[el.name] || '').trim()) bad.push([el.name, el.getAttribute('data-required-msg') || 'Please fill this in']);
      });
      if (!signedIn && form.elements.email && !EMAIL_RE.test(v.email || '')) bad.push(['email', 'Please enter a valid email address']);
      // Optional on the Contact form: checked only when it is required or given.
      var ph = form.elements.phone;
      if (ph && (ph.required || String(v.phone || '').trim()) && String(v.phone || '').replace(/\D/g, '').length < 10) bad.push(['phone', 'Please enter a valid UK phone number']);
      return bad;
    }
    function values() {
      var v = {};
      Array.prototype.forEach.call(form.elements, function (el) {
        if (!el.name || el.type === 'file' || el.type === 'submit' || el.type === 'button') return;
        if (el.type === 'checkbox') v[el.name] = el.checked;
        else if (el.type === 'radio') { if (el.checked) v[el.name] = el.value; else if (!(el.name in v)) v[el.name] = ''; }
        else v[el.name] = el.name === 'website' ? el.value : el.value.trim();
      });
      if (signedIn) v.email = '';
      v.style = style;
      v.elapsed_ms = Date.now() - shownAt;
      return v;
    }

    function finish(v, failedFiles) {
      form.hidden = true;
      if (styleBox) styleBox.hidden = true;
      if (!done) return;
      var nameEl = done.querySelector('[data-enq-done-name]');
      if (nameEl) nameEl.textContent = v.first_name ? ', ' + v.first_name : '';
      var warn = done.querySelector('[data-enq-done-warn]');
      if (warn && failedFiles && failedFiles.length) {
        warn.textContent = 'We have your details, but ' + (failedFiles.length === 1 ? 'this file' : 'these files')
          + ' did not upload: ' + failedFiles.join(', ') + '. Please mention it when we contact you and we will help you send '
          + (failedFiles.length === 1 ? 'it' : 'them') + '.';
        warn.hidden = false;
      }
      done.hidden = false;
      done.focus();
      done.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      if (submit.disabled) return;
      clearMsgs();
      var v = values();
      var bad = check(v);
      if (bad.length) {
        bad.forEach(function (b) { fieldMsg(b[0], b[1]); });
        var first = form.elements[bad[0][0]] || form.querySelector('[data-kind="' + bad[0][0] + '"]');
        if (first && first.focus) first.focus();
        showError('Please check the highlighted fields.');
        return;
      }
      busy('Sending…');
      postJSON(key, v).then(function (r) {
        if (r.res.ok) {
          var hasFiles = Object.keys(picked).some(function (k) { return picked[k].length; });
          if (hasFiles && r.data && r.data.upload) return uploadAll(r.data.upload).then(function (failed) { finish(v, failed); });
          // Files chosen but no way to send them: say so rather than drop them quietly.
          var unsent = [];
          if (hasFiles) Object.keys(picked).forEach(function (k) { picked[k].forEach(function (f) { unsent.push(f.name); }); });
          finish(v, unsent);
          return;
        }
        var d = r.data || {};
        if (r.res.status === 400 && d.fields && d.fields.length) {
          d.fields.forEach(function (f) { fieldMsg(f.field, f.message); });
          showError('Please check the highlighted fields.');
        } else if (r.res.status === 429) {
          showError('We have already received a few enquiries from you. We will be in touch'
            + (phoneNo ? ', or call us on ' + phoneNo : '') + '.');
        } else {
          showError(trouble());
        }
        busy('');
      }).catch(function () {
        showError(trouble());
        busy('');
      });
    });
  }

  function start() { document.querySelectorAll('form[data-enquiry-form]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
